import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MappedLoanOfficer } from '@/services/mappers/loanOfficer.mapper';
import type { MappedBranch } from '@/services/mappers/branch.mapper';

/**
 * Loan Setup Data - selections from the setup page
 */
export interface LoanSetupData {
  loanType?: string;
  propertyAvailable?: string;
  coBorrower?: string;
}

/**
 * Application Form Data - loan officer and branch selections
 */
export interface LoanApplicationData {
  // User details
  firstName?: string;
  lastName?: string;
  email?: string;
  // Loan Officer
  loanOfficerId?: string;
  loanOfficerNmlsId?: string;
  loanOfficerName?: string;
  loanOfficerPhoto?: string;
  loanOfficer?: string; // Kept for backward compatibility
  loanOfficerData?: MappedLoanOfficer;
  // Branch
  branchData?: MappedBranch;
  // Progress tracking
  activeStep: number;
  completedSteps: number[];
}

interface LoanApplicationState {
  // Loan Setup selections
  loanSetup: LoanSetupData;
  // Application data (loan officer, branch, progress)
  application: LoanApplicationData;
}

const initialState: LoanApplicationState = {
  loanSetup: {},
  application: {
    activeStep: 0,
    completedSteps: [],
  },
};

const loanApplicationSlice = createSlice({
  name: 'loanApplication',
  initialState,
  reducers: {
    // Loan Setup actions
    updateLoanSetup: (state, action: PayloadAction<Partial<LoanSetupData>>) => {
      state.loanSetup = { ...state.loanSetup, ...action.payload };
    },
    clearLoanSetup: (state) => {
      state.loanSetup = {};
    },

    // Application data actions
    updateApplicationData: (
      state,
      action: PayloadAction<Partial<LoanApplicationData>>
    ) => {
      state.application = { ...state.application, ...action.payload };
    },
    clearApplicationData: (state) => {
      state.application = {
        activeStep: 0,
        completedSteps: [],
      };
    },

    // Progress actions
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.application.activeStep = action.payload;
    },
    setCompletedSteps: (state, action: PayloadAction<number[]>) => {
      state.application.completedSteps = action.payload;
    },

    // Clear all loan data
    clearAllLoanData: (state) => {
      state.loanSetup = {};
      state.application = {
        activeStep: 0,
        completedSteps: [],
      };
    },
  },
});

export const {
  updateLoanSetup,
  clearLoanSetup,
  updateApplicationData,
  clearApplicationData,
  setActiveStep,
  setCompletedSteps,
  clearAllLoanData,
} = loanApplicationSlice.actions;

export default loanApplicationSlice.reducer;
